import { useMemo, useState, type FormEvent } from 'react';
import { AlertTriangle, Copy, Download, ExternalLink, KeyRound, Monitor, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '@components/common/Button';
import { Card, CardTitle } from '@components/common/Card';
import { useClaimQbdPairing, useRequestQbdCompanyFileSwitch, useStartQbdInstallSession } from '@api/hooks/useErpConnection';
import { getErpProviderLabel } from '@api/quickbooksAuth';
import { useNotificationStore } from '@store/notificationStore';
import { formatDateTime } from '@utils/formatters';
import type { ERPConnectionDetailResponse, QbdInstallSessionResponse } from '@schemas/api';

interface QuickBooksDesktopConnectorSettingsProps {
  connections: ERPConnectionDetailResponse[];
}

const normalizePairingCode = (value: string) =>
  value
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6);

const isValidPairingCode = (value: string) => value.length >= 5 && value.length <= 6;

const getConnectionName = (connection: ERPConnectionDetailResponse) =>
  connection.display_name || connection.external_company_name || connection.realm_id;

const getConnectionStatusClasses = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'needs_reconnect':
    case 'pending_switch':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'disconnected':
    case 'archived':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
  }
};

const getQbdDiagnosticsMessage = (connection: ERPConnectionDetailResponse) => {
  const diagnostics = connection.diagnostics;
  if (diagnostics?.switch_status === 'requested') {
    return 'Switch requested. The Windows tray will open this company file.';
  }

  if (diagnostics?.switch_status === 'failed') {
    return diagnostics.switch_error?.message || 'The Windows tray could not switch to this company file.';
  }

  if (!diagnostics?.requires_user_action) {
    return null;
  }

  const error = diagnostics.latest_job_error;
  if (error?.message) {
    return error.message;
  }

  const path = diagnostics.company_file_path;
  return path
    ? `Open ${path} in QuickBooks Desktop.`
    : 'Open the registered QuickBooks Desktop company file.';
};

export const QuickBooksDesktopConnectorSettings = ({
  connections,
}: QuickBooksDesktopConnectorSettingsProps) => {
  const qbdConnections = useMemo(
    () => connections.filter((connection) => connection.erp_type === 'quickbooks_desktop'),
    [connections],
  );
  const startInstallSession = useStartQbdInstallSession();
  const claimPairing = useClaimQbdPairing();
  const requestCompanyFileSwitch = useRequestQbdCompanyFileSwitch();
  const { addNotification } = useNotificationStore();
  const [installSession, setInstallSession] = useState<QbdInstallSessionResponse | null>(null);
  const [pairingCode, setPairingCode] = useState('');
  const [displayName, setDisplayName] = useState('QuickBooks Desktop');
  const [country, setCountry] = useState('US');

  const handleStartSession = () => {
    startInstallSession.mutate(undefined, {
      onSuccess: (session) => {
        setInstallSession(session);
        addNotification({
          type: 'success',
          message: 'QuickBooks Desktop setup is ready',
        });
      },
      onError: () => {
        addNotification({
          type: 'error',
          message: 'Failed to start QuickBooks Desktop setup',
        });
      },
    });
  };

  const handleOpenConnector = () => {
    if (!installSession?.launch_url) {
      return;
    }
    window.location.href = installSession.launch_url;
  };

  const handleCopyLaunchLink = async () => {
    if (!installSession?.launch_url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(installSession.launch_url);
      addNotification({ type: 'success', message: 'Connector launch link copied' });
    } catch {
      addNotification({ type: 'error', message: 'Failed to copy connector launch link' });
    }
  };

  const handleClaimPairing = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedCode = normalizePairingCode(pairingCode);
    if (!isValidPairingCode(normalizedCode)) {
      addNotification({ type: 'error', message: 'Enter the pairing code from the tray' });
      return;
    }

    claimPairing.mutate(
      {
        pairingCode: normalizedCode,
        payload: {
          display_name: displayName.trim() || 'QuickBooks Desktop',
          country: country.trim().toUpperCase() || 'US',
          capabilities: { initiated_from: 'admin_integrations' },
        },
      },
      {
        onSuccess: () => {
          setPairingCode('');
          setInstallSession(null);
        },
      },
    );
  };

  return (
    <Card>
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-primary-600" />
              <CardTitle className="text-xl">QuickBooks Desktop</CardTitle>
            </div>
            <p className="mt-1 text-sm text-content-secondary">
              Tenant-scoped connector setup for Windows company files.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={handleStartSession}
            loading={startInstallSession.isPending}
            className="inline-flex items-center justify-center gap-2"
          >
            <ShieldCheck className="h-4 w-4" />
            Start Setup
          </Button>
        </div>

        {qbdConnections.length > 0 && (
          <div className="overflow-x-auto border-y border-gray-200 dark:border-gray-700">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-content-secondary">
                <tr>
                  <th className="px-2 py-2">Company File</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Last Sync</th>
                  <th className="px-2 py-2">Connector</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {qbdConnections.map((connection) => {
                  const diagnosticsMessage = getQbdDiagnosticsMessage(connection);
                  const switchPending = connection.diagnostics?.switch_status === 'requested';
                  const canRequestSwitch = Boolean(connection.diagnostics?.company_file_path) && !switchPending;
                  return (
                    <tr key={connection.id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-2 py-2">
                        <div className="font-medium text-content-primary">{getConnectionName(connection)}</div>
                        <div className="text-xs text-content-muted">
                          {connection.diagnostics?.company_file_path || connection.realm_id}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`inline-flex rounded px-2 py-1 text-xs font-medium ${getConnectionStatusClasses(connection.status)}`}
                        >
                          {connection.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-content-secondary">
                        {connection.last_synced_at ? formatDateTime(connection.last_synced_at) : 'Not synced'}
                      </td>
                      <td className="px-2 py-2">
                        {diagnosticsMessage ? (
                          <div className="flex max-w-sm items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{diagnosticsMessage}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-content-secondary">
                            {connection.diagnostics?.last_heartbeat_at
                              ? `Seen ${formatDateTime(connection.diagnostics.last_heartbeat_at)}`
                              : 'No connector heartbeat yet'}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!canRequestSwitch || requestCompanyFileSwitch.isPending}
                          loading={requestCompanyFileSwitch.isPending}
                          onClick={() => requestCompanyFileSwitch.mutate(connection.id)}
                          className="inline-flex items-center gap-2"
                        >
                          <RefreshCw className="h-4 w-4" />
                          {switchPending ? 'Requested' : 'Switch'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {installSession && (
          <div className="rounded border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-950/30">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="text-sm">
                <div className="font-medium text-content-primary">
                  {installSession.tenant_name || 'Tenant'} setup expires {formatDateTime(installSession.expires_at)}
                </div>
                <div className="mt-1 break-all text-xs text-content-secondary">
                  {installSession.tenant_id}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {installSession.installer_download_url && (
                  <a
                    href={installSession.installer_download_url}
                    className="btn-secondary inline-flex items-center gap-2 px-3 py-2 text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </a>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleOpenConnector}
                  className="inline-flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Connector
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyLaunchLink}
                  className="inline-flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Copy Link
                </Button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleClaimPairing} className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_8rem_9rem_auto]">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-content-secondary">Display Name</span>
            <input
              type="text"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              className="input"
              autoComplete="off"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-content-secondary">Pairing Code</span>
            <input
              type="text"
              value={pairingCode}
              onChange={(event) => setPairingCode(normalizePairingCode(event.target.value))}
              className="input font-mono uppercase"
              autoComplete="one-time-code"
              maxLength={7}
              placeholder="ABC-123"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-content-secondary">Country</span>
            <input
              type="text"
              value={country}
              onChange={(event) => setCountry(event.target.value.toUpperCase().slice(0, 2))}
              className="input uppercase"
              autoComplete="country"
              maxLength={2}
            />
          </label>
          <div className="flex items-end">
            <Button
              type="submit"
              variant="success"
              loading={claimPairing.isPending}
              className="inline-flex w-full items-center justify-center gap-2 lg:w-auto"
            >
              <KeyRound className="h-4 w-4" />
              Pair
            </Button>
          </div>
        </form>

        {qbdConnections.length === 0 && (
          <div className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm text-content-secondary dark:border-gray-700">
            <RefreshCw className="h-4 w-4" />
            No {getErpProviderLabel('quickbooks_desktop')} company files linked. Pair the connector, then add a .qbw file from the Windows tray.
          </div>
        )}
      </div>
    </Card>
  );
};
